"use client";

import React, { useState } from "react";
import { Bot, Copy, Check, Clock, User, Mic } from "lucide-react";
import type { Message } from "@/types";

interface MessageItemProps {
  message: Message;
}

function isArabicText(text: string): boolean {
  const arabicPattern = /[\u0600-\u06FF\u0750-\u077F\u08A0-\u08FF]/;
  return arabicPattern.test(text);
}

export function MessageItem({ message }: MessageItemProps) {
  const isUser = message.role === "user";
  const [isCopied, setIsCopied] = useState(false);
  const isArabic = isArabicText(message.content);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(message.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error("Failed to copy text:", err);
    }
  };

  const formatResponseTime = (responseTime: number) => {
    if (responseTime < 1000) {
      return `${responseTime}ms`;
    }
    return `${(responseTime / 1000).toFixed(2)}s`;
  };

  return (
    <div
      className={`flex items-start gap-3 md:gap-4 ${
        isUser ? "flex-row-reverse" : ""
      }`}
    >
      {/* Avatar */}
      <div className="flex flex-col items-center gap-2">
        <div
          className="w-9 h-9 md:w-10 md:h-10 rounded-full flex items-center justify-center shrink-0 shadow-lg"
          style={{
            backgroundColor: isUser ? "#d3a035" : "#0b015d",
          }}
        >
          {isUser ? (
            message.isVoice ? (
              <Mic className="w-4 h-4 md:w-5 md:h-5 text-white" />
            ) : (
              <User className="w-4 h-4 md:w-5 md:h-5 text-white" />
            )
          ) : (
            <Bot className="w-4 h-4 md:w-5 md:h-5 text-white" />
          )}
        </div>

        {/* Timestamp */}
        <div className="hidden md:block text-xs text-gray-400 text-center whitespace-nowrap">
          {message.timestamp.toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          })}
        </div>
      </div>

      {/* Message Content */}
      <div
        className={`flex-1 max-w-[85%] md:max-w-[75%] ${
          isUser ? "text-right" : ""
        }`}
      >
        <div
          className="inline-block px-4 py-3 rounded-2xl transition-all duration-300 shadow-sm"
          style={{
            backgroundColor: isUser ? "#d3a035" : "white",
            color: isUser ? "white" : "#0b015d",
            border: isUser ? "none" : "1px solid #e5e7eb",
          }}
          dir={isArabic ? "rtl" : "ltr"}
        >
          <p className="leading-relaxed whitespace-pre-wrap text-sm md:text-base">
            {message.content}
          </p>
        </div>

        {/* Action Buttons */}
        <div
          className={`flex items-center gap-2 mt-2 px-2 ${
            isUser ? "justify-end" : "justify-start"
          }`}
        >
          {/* Copy Button */}
          <button
            onClick={handleCopy}
            className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-all duration-200"
            title={isCopied ? "Copied!" : "Copy message"}
          >
            {isCopied ? (
              <Check size={14} className="text-green-500" />
            ) : (
              <Copy size={14} />
            )}
          </button>

          {/* Response Time - Only for assistant messages */}
          {!isUser && message.responseTime && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-gray-100 text-gray-500">
              <Clock size={12} />
              <span className="text-xs">
                {formatResponseTime(message.responseTime)}
              </span>
            </div>
          )}

          {/* Voice indicator */}
          {isUser && message.isVoice && (
            <div className="flex items-center gap-1 px-2 py-1 rounded-lg bg-blue-50 text-blue-500">
              <Mic size={12} />
              <span className="text-xs">Voice</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
